import { apiGet, apiPost, apiPatch, apiDelete, API_BASE } from "./client.js";
import type {
  AdminUser,
  AdminStats,
  AdminPlan,
  BillingEvent,
  SubscriptionWithPlan,
} from "./types.js";

export interface ListUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

export interface UserDetailResponse {
  user: AdminUser;
  orgs: Array<{ id: string; name: string; role: string }>;
  subscription: SubscriptionWithPlan | null;
  websites: Array<{ id: string; url: string; domain: string }>;
}

export function listUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<ListUsersResponse> {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.search) qs.set("search", params.search);
  const q = qs.toString();
  return apiGet<ListUsersResponse>(`/admin/users${q ? `?${q}` : ""}`);
}

export function getUser(userId: string): Promise<UserDetailResponse> {
  return apiGet<UserDetailResponse>(`/admin/users/${userId}`);
}

export function updateSubscriptionOverrides(
  userId: string,
  overrides: {
    overrideMaxWebsites?: number | null;
    overrideMaxPagesPerScan?: number | null;
    overrideScansPerMonth?: number | null;
    overrideDocsEnabled?: number | null;
    overrideRecurringEnabled?: number | null;
    overrideNotes?: string | null;
  },
): Promise<{ ok: boolean; subscription: unknown }> {
  return apiPatch<{ ok: boolean; subscription: unknown }>(
    `/admin/users/${userId}/subscription`,
    overrides,
  );
}

export function setUserDisabled(
  userId: string,
  disabled: boolean,
): Promise<{ ok: boolean }> {
  return apiPatch<{ ok: boolean }>(
    `/admin/users/${userId}/disable`,
    { disabled },
  );
}

export function impersonateUser(
  userId: string,
  reason: string,
): Promise<{ ok: boolean; redirectUrl: string }> {
  return apiPost<{ ok: boolean; redirectUrl: string }>(
    `/admin/users/${userId}/impersonate`,
    { reason },
  );
}

export function getBillingEvents(params?: {
  page?: number;
  limit?: number;
  type?: string;
  fromDate?: string;
}): Promise<{
  events: BillingEvent[];
  total: number;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.type) qs.set("type", params.type);
  if (params?.fromDate) qs.set("fromDate", params.fromDate);
  const q = qs.toString();
  return apiGet(`/admin/billing-events${q ? `?${q}` : ""}`);
}

export function getStats(): Promise<AdminStats> {
  return apiGet<AdminStats>("/admin/stats");
}

// ── Plan management ────────────────────────────────────────────────────────

export function listPlans(): Promise<{ plans: AdminPlan[] }> {
  return apiGet<{ plans: AdminPlan[] }>("/admin/plans");
}

export function getPlan(
  planId: string,
): Promise<{ plan: AdminPlan }> {
  return apiGet<{ plan: AdminPlan }>(`/admin/plans/${planId}`);
}

export function updatePlan(
  planId: string,
  updates: Partial<
    Omit<AdminPlan, "id" | "created_at" | "subscriberCount">
  >,
): Promise<{ ok: boolean; plan: AdminPlan }> {
  return apiPatch<{ ok: boolean; plan: AdminPlan }>(
    `/admin/plans/${planId}`,
    updates,
  );
}

export function createPlan(plan: {
  name: string;
  stripePriceId?: string | null;
  priceEurCents: number;
  maxWebsites: number;
  scansPerMonthPerWebsite: number;
  maxPagesPerScan: number;
  docsEnabled?: boolean;
  recurringEnabled?: boolean;
  scanHistoryMonths?: number;
  sortOrder?: number;
}): Promise<{ ok: boolean; plan: AdminPlan }> {
  return apiPost<{ ok: boolean; plan: AdminPlan }>(`/admin/plans`, plan);
}

// ── Global admin search ───────────────────────────────────────────────────

export interface AdminSearchResult {
  users: Array<{ id: string; email: string; disabled: boolean }>;
  orgs: Array<{ id: string; name: string }>;
  websites: Array<{ id: string; url: string; domain: string }>;
}

export function adminSearch(q: string): Promise<AdminSearchResult> {
  return apiGet<AdminSearchResult>(
    `/admin/search?q=${encodeURIComponent(q)}`,
  );
}

// ── Audit logs ────────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  admin_user_id: string;
  action: string;
  target_user_id: string | null;
  target_org_id: string | null;
  details_json: string;
  occurred_at: string;
}

export function getAuditLogs(params?: {
  page?: number;
  limit?: number;
}): Promise<{
  logs: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiGet(`/admin/audit-logs${q ? `?${q}` : ""}`);
}

// ── Billing export download ──────────────────────────────────────────────

export type BillingExportType = "revenue" | "customers" | "payments";

/**
 * Download a billing export CSV via the admin endpoint.
 * Triggers a browser file-save dialog.
 */
export async function downloadBillingExport(
  type: BillingExportType,
): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/billing-export/${type}`, {
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { message?: string }).message ??
        `Export failed (${res.status})`,
    );
  }

  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="(.+?)"/);
  const filename = match?.[1] ?? `${type}_export.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Gift Scans ────────────────────────────────────────────────────────────

export interface ScanCredit {
  id: string;
  orgId: string;
  websiteId: string | null;
  amount: number;
  remaining: number;
  reason: string;
  giftedBy: string;
  giftedByEmail: string;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
  isFullyUsed: boolean;
}

export function giftScans(
  orgId: string,
  params: { amount: number; reason: string; websiteId?: string | null },
): Promise<{ ok: boolean; credit: ScanCredit }> {
  return apiPost<{ ok: boolean; credit: ScanCredit }>(
    `/admin/orgs/${orgId}/gift-scans`,
    params,
  );
}

export function bulkGiftScans(params: {
  userIds: string[];
  amount: number;
  reason: string;
}): Promise<{
  ok: boolean;
  results: Array<{ userId: string; orgId?: string; ok: boolean; error?: string }>;
}> {
  return apiPost(`/admin/gift-scans/bulk-users`, params);
}

export function getGiftHistory(
  orgId: string,
  params?: { page?: number; limit?: number },
): Promise<{
  credits: ScanCredit[];
  total: number;
  page: number;
  limit: number;
}> {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiGet(`/admin/orgs/${orgId}/gift-history${q ? `?${q}` : ""}`);
}

// ── Webhook Replay ───────────────────────────────────────────────────────

export function replayBillingEvent(
  eventId: string,
): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>(
    `/admin/billing-events/${eventId}/replay`,
    {},
  );
}

// ── GDPR Admin Tools ─────────────────────────────────────────────────────

export async function downloadUserExport(userId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/export`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { message?: string }).message ?? `Export failed (${res.status})`,
    );
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `user_export_${userId}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function deleteUserAccount(
  userId: string,
): Promise<{ ok: boolean }> {
  return apiDelete<{ ok: boolean }>(`/admin/users/${userId}`);
}
